import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Export dashboard to PDF - Smart pagination to avoid cutting widgets
 * Method: Capture entire dashboard as one image, then find smart page break points
 * @param {string} elementId - ID of the DOM element to export
 * @param {string} filename - Name of the PDF file
 * @param {object} options - Additional options
 */
export const exportToPDF = async (elementId, filename = 'report.pdf', options = {}) => {
    try {
        const element = document.getElementById(elementId);
        if (!element) {
            console.error(`Element with id "${elementId}" not found`);
            return;
        }

        // Show loading state if callback provided
        if (options.onStart) {
            options.onStart();
        }

        // Capture the ENTIRE dashboard as one image
        const canvas = await html2canvas(element, {
            scale: 2, // Higher quality
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            ...options.canvasOptions
        });

        // Get widget positions to find safe page break points
        const widgets = element.querySelectorAll('[class*="react-grid-item"]');
        const containerRect = element.getBoundingClientRect();

        // Collect widget Y boundaries (top and bottom positions)
        const widgetBoundaries = Array.from(widgets).map(widget => {
            const rect = widget.getBoundingClientRect();
            return {
                top: rect.top - containerRect.top,
                bottom: rect.bottom - containerRect.top
            };
        });

        // PDF dimensions
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pageWidthMM = 210;
        const pageHeightMM = 297;
        const marginMM = 5;
        const headerHeightMM = 20; // Space for title and date
        const contentWidthMM = pageWidthMM - (marginMM * 2);
        const contentHeightMM = pageHeightMM - (marginMM * 2) - headerHeightMM;

        // Calculate scale factor (pixels to mm)
        const imgWidthPx = canvas.width;
        const imgHeightPx = canvas.height;
        const scale = contentWidthMM / (imgWidthPx / 2); // Divide by 2 because canvas scale is 2
        const imgHeightMM = (imgHeightPx / 2) * scale;

        // Convert page height to pixels (at original element scale)
        const pageHeightPx = contentHeightMM / scale;

        // Find safe page break points (positions where no widget is cut)
        const safeBreakPoints = findSafePageBreaks(widgetBoundaries, pageHeightPx, containerRect.height);

        // Generate PDF pages
        const imgData = canvas.toDataURL('image/png');
        let currentPage = 0;
        const totalPages = safeBreakPoints.length;

        // Get entity name and date for header
        const entityName = options.scopeName || 'Default';
        const exportDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        for (let i = 0; i < safeBreakPoints.length; i++) {
            const startY = i === 0 ? 0 : safeBreakPoints[i - 1];
            const endY = safeBreakPoints[i];

            if (currentPage > 0) {
                pdf.addPage();
            }

            // Add header on each page
            pdf.setFontSize(14);
            pdf.setTextColor(0, 120, 212); // Blue
            pdf.text(`Analytics Report - Entity: ${entityName}`, marginMM, 12);

            pdf.setFontSize(9);
            pdf.setTextColor(100, 100, 100);
            pdf.text(exportDate, marginMM, 18);

            pdf.setFontSize(8);
            pdf.text(`Page ${currentPage + 1} / ${totalPages}`, pageWidthMM - marginMM - 25, 18);

            // Draw line under header
            pdf.setDrawColor(200, 200, 200);
            pdf.line(marginMM, 22, pageWidthMM - marginMM, 22);

            // Calculate source and destination coordinates
            const srcY = startY * 2; // Multiply by scale (2)
            const srcHeight = (endY - startY) * 2;

            // Create a cropped canvas for this page section
            const pageCanvas = document.createElement('canvas');
            pageCanvas.width = canvas.width;
            pageCanvas.height = srcHeight;
            const ctx = pageCanvas.getContext('2d');
            ctx.drawImage(canvas, 0, srcY, canvas.width, srcHeight, 0, 0, canvas.width, srcHeight);

            const pageImgData = pageCanvas.toDataURL('image/png');
            const pageImgHeightMM = (srcHeight / 2) * scale;

            // Add cropped image to PDF (after header)
            pdf.addImage(pageImgData, 'PNG', marginMM, marginMM + headerHeightMM, contentWidthMM, pageImgHeightMM);

            currentPage++;
        }

        // Add metadata
        pdf.setProperties({
            title: filename,
            subject: 'Analytics Report',
            author: 'Call Center Analytics',
            keywords: 'analytics, report, call center',
            creator: 'Admin Portal'
        });

        // Save PDF
        pdf.save(filename);

        // Success callback
        if (options.onSuccess) {
            options.onSuccess();
        }

        return pdf;
    } catch (error) {
        console.error('Error exporting to PDF:', error);
        if (options.onError) {
            options.onError(error);
        }
        throw error;
    }
};

/**
 * Find safe page break points where no widget will be cut
 * @param {Array} widgetBoundaries - Array of {top, bottom} for each widget
 * @param {number} pageHeightPx - Height of one page in pixels
 * @param {number} totalHeightPx - Total height of the content in pixels
 * @returns {Array} Array of Y positions (in pixels) where pages should end
 */
function findSafePageBreaks(widgetBoundaries, pageHeightPx, totalHeightPx) {
    const breakPoints = [];
    let currentPosition = 0;

    while (currentPosition < totalHeightPx) {
        // Ideal next page break
        let idealBreak = currentPosition + pageHeightPx;

        if (idealBreak >= totalHeightPx) {
            // Last page - include everything
            breakPoints.push(totalHeightPx);
            break;
        }

        // Find the best safe break point
        let safeBreak = idealBreak;

        // Check ALL widgets that might be cut at the ideal break position
        for (const widget of widgetBoundaries) {
            // Widget is cut if break point is between its top and bottom
            if (idealBreak > widget.top && idealBreak < widget.bottom) {
                // This widget would be cut - we must break BEFORE it
                const breakBeforeWidget = widget.top - 5;

                // Only use this break if it's after current position and leaves reasonable content
                if (breakBeforeWidget > currentPosition + 50) {
                    // Take the earliest safe break (in case multiple widgets would be cut)
                    if (breakBeforeWidget < safeBreak) {
                        safeBreak = breakBeforeWidget;
                    }
                }
            }
        }

        // Ensure minimum content per page
        if (safeBreak <= currentPosition + 50) {
            safeBreak = idealBreak; // Force break even if widget is cut
        }

        breakPoints.push(safeBreak);
        currentPosition = safeBreak;
    }

    return breakPoints;
}

/**
 * Export multiple widgets or sections to a single PDF
 * @param {Array} elementIds - Array of element IDs to export
 * @param {string} filename - Name of the PDF file
 * @param {object} options - Additional options
 */
export const exportMultipleToPDF = async (elementIds, filename = 'report.pdf', options = {}) => {
    try {
        if (options.onStart) {
            options.onStart();
        }

        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210;
        let isFirstPage = true;

        for (const elementId of elementIds) {
            const element = document.getElementById(elementId);
            if (!element) {
                console.warn(`Element with id "${elementId}" not found, skipping...`);
                continue;
            }

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                ...options.canvasOptions
            });

            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            const imgData = canvas.toDataURL('image/png');

            if (!isFirstPage) {
                pdf.addPage();
            }

            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
            isFirstPage = false;
        }

        // Add metadata
        pdf.setProperties({
            title: filename,
            subject: 'Analytics Report',
            author: 'Call Center Analytics',
            keywords: 'analytics, report, call center',
            creator: 'Admin Portal'
        });

        pdf.save(filename);

        if (options.onSuccess) {
            options.onSuccess();
        }

        return pdf;
    } catch (error) {
        console.error('Error exporting multiple elements to PDF:', error);
        if (options.onError) {
            options.onError(error);
        }
        throw error;
    }
};

/**
 * Export multi-tab analytics report to a single PDF with cover page.
 * Iterates each tab via onTabSwitch, waits for render, captures, paginates intelligently.
 *
 * @param {object} params
 * @param {Array<{id, displayName}>} params.tabs - Tabs to capture in order (cover excluded)
 * @param {string} params.elementId - DOM id captured for each tab (typically 'dashboard-container')
 * @param {string} params.filename
 * @param {object} params.coverPageData - { title, scope, dateRange, generatedAt }
 * @param {Function} params.onTabSwitch - async (tabId) => void — switches tab + waits for render
 * @param {Function} [params.onProgress] - (current, total, tabName) => void
 */
export const exportTabsToPDF = async ({
    tabs,
    elementId,
    filename = 'analytics-report.pdf',
    coverPageData = {},
    // Libellés du document, DÉJÀ TRADUITS par l'appelant. Cet util ne connaît pas i18n :
    // le PDF était intégralement en anglais en dur (Period, Contents, Page X / Y…) même
    // pour un utilisateur en français. Les valeurs ci-dessous ne sont qu'un repli.
    labels = {},
    onTabSwitch,
    onProgress
}) => {
    const L = {
        period: 'Period',
        generated: 'Generated',
        contents: 'Contents',
        page: 'Page',
        continued: 'cont.',
        ...labels
    };
    // `compress: true` : jsPDF construit le document comme UNE SEULE chaîne JavaScript.
    // Sans compression, un rapport de 9 onglets dépassait la taille maximale d'une string
    // dans le moteur JS → « Invalid string length », et l'export échouait à la toute fin,
    // après plusieurs minutes de capture.
    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;

    // Facteur de suréchantillonnage des captures. UNE SEULE constante : le code utilisait
    // `scale: 3` pour html2canvas mais divisait par 2 dans TOUS les calculs de mise en page
    // (quelqu'un a monté la qualité sans toucher aux diviseurs). Résultat : des canvas 2,25×
    // trop lourds, des images mal dimensionnées, et un découpage en pages trop nombreuses.
    const CANVAS_SCALE = 2;

    // JPEG plutôt que PNG : le fond est forcé en blanc (aucune transparence à préserver), et
    // une capture de dashboard en PNG produit une chaîne base64 démesurée. Le JPEG divise le
    // poids par ~10 pour une perte invisible sur des graphiques.
    const IMAGE_FORMAT = 'JPEG';
    const IMAGE_QUALITY = 0.92;
    const toImage = (cnv) => cnv.toDataURL('image/jpeg', IMAGE_QUALITY);

    // Les polices standard de jsPDF (Helvetica…) sont encodées en WinAnsi : tout caractère
    // hors de ce jeu sort en charabia. La flèche « → » de la période s'imprimait « !’ ».
    // Les accents latins (é, à, ç…) sont dans WinAnsi et passent sans problème — on ne
    // remplace donc QUE les signes typographiques absents du jeu.
    const pdfSafe = (text) => String(text ?? '')
        .replace(/[→⟶←⟵]/g, '-')
        .replace(/[‘’]/g, "'")
        .replace(/[“”]/g, '"')
        .replace(/…/g, '...')
        .replace(/[–—]/g, '-');

    // === COVER PAGE ===
    pdf.setFillColor(91, 95, 199); // Heedify accent
    pdf.rect(0, 0, pageWidth, 60, 'F');

    pdf.setFontSize(28);
    pdf.setTextColor(255, 255, 255);
    pdf.text(pdfSafe(coverPageData.title || 'Analytics Report'), pageWidth / 2, 35, { align: 'center' });

    pdf.setFontSize(22);
    pdf.setTextColor(27, 58, 107);
    pdf.text(pdfSafe(coverPageData.scope || 'Default'), pageWidth / 2, 95, { align: 'center' });

    pdf.setFontSize(14);
    pdf.setTextColor(100, 100, 100);
    pdf.text(pdfSafe(L.period), pageWidth / 2, 120, { align: 'center' });
    pdf.setFontSize(16);
    pdf.setTextColor(50, 49, 48);
    pdf.text(pdfSafe(coverPageData.dateRange || '-'), pageWidth / 2, 130, { align: 'center' });

    pdf.setFontSize(12);
    pdf.setTextColor(120, 120, 120);
    pdf.text(pdfSafe(`${L.generated} ${coverPageData.generatedAt || new Date().toLocaleString()}`), pageWidth / 2, 165, { align: 'center' });

    // Table of contents
    pdf.setFontSize(14);
    pdf.setTextColor(27, 58, 107);
    pdf.text(pdfSafe(L.contents), margin + 10, 200);
    pdf.setDrawColor(91, 95, 199);
    pdf.line(margin + 10, 203, margin + 50, 203);

    pdf.setFontSize(12);
    pdf.setTextColor(50, 49, 48);
    let tocY = 215;
    tabs.forEach((tab, i) => {
        pdf.text(`${i + 1}.`, margin + 15, tocY);
        pdf.text(pdfSafe(tab.displayName), margin + 25, tocY);
        tocY += 8;
    });

    // === TABS ===
    for (let i = 0; i < tabs.length; i++) {
        const tab = tabs[i];
        if (onProgress) onProgress(i + 1, tabs.length, tab.displayName);

        // Switch to tab and wait for render
        if (onTabSwitch) await onTabSwitch(tab.id);

        const element = document.getElementById(elementId);
        if (!element) {
            console.warn(`[pdf] elementId "${elementId}" not found for tab "${tab.id}", skipping`);
            continue;
        }

        const canvas = await html2canvas(element, {
            scale: CANVAS_SCALE,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            allowTaint: false,
            // Force layout settle before snapshot
            onclone: (clonedDoc) => {
                // Disable any remaining CSS animations/transitions in the cloned DOM
                const style = clonedDoc.createElement('style');
                style.textContent = `
                    *, *::before, *::after {
                        animation-duration: 0s !important;
                        animation-delay: 0s !important;
                        transition-duration: 0s !important;
                        transition-delay: 0s !important;
                    }
                `;
                clonedDoc.head.appendChild(style);
            }
        });

        // Section title page
        pdf.addPage();
        pdf.setFontSize(20);
        pdf.setTextColor(27, 58, 107);
        pdf.text(pdfSafe(tab.displayName), margin, 20);
        pdf.setDrawColor(91, 95, 199);
        pdf.setLineWidth(0.8);
        pdf.line(margin, 24, pageWidth - margin, 24);
        pdf.setLineWidth(0.2);

        const contentTop = 32;
        const availableHeight = pageHeight - contentTop - margin;

        // mm par pixel de canvas. TOUS les calculs passent par CANVAS_SCALE — c'est la
        // désynchronisation entre le scale de capture et ces diviseurs qui cassait la mise
        // en page et gonflait le poids du document.
        const scale = contentWidth / (canvas.width / CANVAS_SCALE);
        const totalImgHeightMM = (canvas.height / CANVAS_SCALE) * scale;

        if (totalImgHeightMM <= availableHeight) {
            // Fits on one page
            pdf.addImage(toImage(canvas), IMAGE_FORMAT, margin, contentTop, contentWidth, totalImgHeightMM);
        } else {
            // Paginate by slicing canvas
            const pageHeightInCanvasPx = (availableHeight / scale) * CANVAS_SCALE;
            let yOffset = 0;
            let firstSlice = true;

            while (yOffset < canvas.height) {
                if (!firstSlice) {
                    pdf.addPage();
                    pdf.setFontSize(11);
                    pdf.setTextColor(120, 120, 120);
                    pdf.text(pdfSafe(`${tab.displayName} (${L.continued})`), margin, 15);
                    pdf.setDrawColor(220, 220, 220);
                    pdf.line(margin, 18, pageWidth - margin, 18);
                }
                firstSlice = false;

                const sliceHeight = Math.min(pageHeightInCanvasPx, canvas.height - yOffset);
                const sliceCanvas = document.createElement('canvas');
                sliceCanvas.width = canvas.width;
                sliceCanvas.height = sliceHeight;
                const ctx = sliceCanvas.getContext('2d');
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, canvas.width, sliceHeight);
                ctx.drawImage(canvas, 0, yOffset, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

                pdf.addImage(
                    toImage(sliceCanvas), IMAGE_FORMAT,
                    margin, contentTop, contentWidth, (sliceHeight / CANVAS_SCALE) * scale
                );

                // Libère le canvas de tranche : sur un rapport long, les garder tous en
                // mémoire jusqu'au GC suffisait à faire tomber l'onglet.
                sliceCanvas.width = 0;
                sliceCanvas.height = 0;

                yOffset += sliceHeight;
            }
        }

        // Idem pour le canvas plein de l'onglet, avant de passer au suivant.
        canvas.width = 0;
        canvas.height = 0;
    }

    // Pieds de page — UNE SEULE PASSE, après TOUS les onglets.
    //
    // Cette boucle était À L'INTÉRIEUR de la boucle des onglets : elle re-tamponnait donc
    // TOUTES les pages déjà produites à chaque nouvel onglet, avec un total différent à
    // chaque fois (le document grandissait au fur et à mesure). Les mentions s'empilaient
    // les unes sur les autres : « Page 2 / 10  Page 2 / 11  Page 2 / 2  Page 2 / 3… ».
    // Le total n'est connu qu'une fois le document terminé — c'est donc ici, et une seule
    // fois, qu'il faut le poser.
    const totalPages = pdf.internal.getNumberOfPages();
    for (let p = 2; p <= totalPages; p++) {   // pas de numéro sur la page de garde
        pdf.setPage(p);
        pdf.setFontSize(9);
        pdf.setTextColor(150, 150, 150);
        pdf.text(pdfSafe(`${L.page} ${p} / ${totalPages}`), pageWidth - margin, pageHeight - 5, { align: 'right' });
    }

    pdf.setProperties({
        title: filename,
        subject: 'Analytics Report',
        author: 'Heedify',
        creator: 'Heedify Admin Portal'
    });

    pdf.save(filename);
};

/**
 * Export with custom header and footer
 * @param {string} elementId - ID of the DOM element to export
 * @param {string} filename - Name of the PDF file
 * @param {object} options - Additional options including header/footer config
 */
export const exportToPDFWithHeaderFooter = async (elementId, filename = 'report.pdf', options = {}) => {
    try {
        const element = document.getElementById(elementId);
        if (!element) {
            console.error(`Element with id "${elementId}" not found`);
            return;
        }

        if (options.onStart) {
            options.onStart();
        }

        const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            ...options.canvasOptions
        });

        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgData = canvas.toDataURL('image/png');

        // Add header
        if (options.header) {
            pdf.setFontSize(16);
            pdf.setTextColor(40, 40, 40);
            pdf.text(options.header.title || 'Analytics Report', 10, 15);

            if (options.header.subtitle) {
                pdf.setFontSize(10);
                pdf.setTextColor(100, 100, 100);
                pdf.text(options.header.subtitle, 10, 22);
            }
        }

        // Add content (with offset for header)
        const contentY = options.header ? 30 : 0;
        const availableHeight = pageHeight - contentY - (options.footer ? 15 : 0);
        const scaledImgHeight = Math.min(imgHeight, availableHeight);

        pdf.addImage(imgData, 'PNG', 0, contentY, imgWidth, scaledImgHeight);

        // Add footer
        if (options.footer) {
            const footerY = pageHeight - 10;
            pdf.setFontSize(8);
            pdf.setTextColor(150, 150, 150);

            const now = new Date();
            const dateStr = now.toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });

            pdf.text(`Generated: ${dateStr}`, 10, footerY);

            if (options.footer.pageNumbers) {
                pdf.text(`Page 1`, imgWidth - 30, footerY);
            }
        }

        pdf.setProperties({
            title: filename,
            subject: 'Analytics Report',
            author: 'Call Center Analytics',
            keywords: 'analytics, report, call center',
            creator: 'Admin Portal'
        });

        pdf.save(filename);

        if (options.onSuccess) {
            options.onSuccess();
        }

        return pdf;
    } catch (error) {
        console.error('Error exporting to PDF with header/footer:', error);
        if (options.onError) {
            options.onError(error);
        }
        throw error;
    }
};
